import { useContext, useEffect, useMemo, useState } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import Button from "@mui/material/Button";
import { Add } from "@mui/icons-material";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  addDoc,
  getDoc,
  collectionGroup,
  setDoc,
  runTransaction,
} from "firebase/firestore";
import CloseIcon from "@mui/icons-material/Close";
import {
  Avatar,
  Box,
  Chip,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Slide,
  Card,
  CardContent,
  Typography,
  Tooltip,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { Autocomplete, TextField } from "@mui/material";
import { db, storage } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { showCustomToast } from "../misc/CustomToast";
import { LanguageContext } from "../context/LanguageContext";
import EditTicketModal from "../forms/EditTicketModal";
import DeleteTicketModal from "../forms/DeleteTicketModal";
import Skeleton from "@mui/material/Skeleton";
import Column from "../misc/Column";
import LeftMenu from "./menus/LeftMenu";
import TicketLineAdmin from "./Lines/TickeTLineAdmin";
import TicketLine from "./Lines/TicketLine";
import HeaderBar from "./headers/HeaderBar";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import UploadFilesModal from "../uploaders/UploadFilesModal";
import { Tabs, Tab } from "@mui/material";
import ArrowHand from "../images/arrow-blue.png";
import { useNavigate, useParams } from "react-router-dom";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useIsPremium } from "../hooks/useIsPremium";
import { useMonthlyTicketUsage } from "../hooks/useMonthlyTicketUsage";

export default function TicketsDesktop() {
  const { translate } = useContext(LanguageContext);
  const [openAddClientModal, setOpenAddClientModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const { currentUser } = useAuth();
  const [ticketName, setTicketName] = useState("");
  const [, setScreenshots] = useState(null);
  const [ticketType, setTicketType] = useState("");
  const [pageList, setPageList] = useState("");
  const [originalQuestion, setOriginalQuestion] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("Low");
  const [, setClientIdState] = useState(currentUser?.uid || "defaultClientId");
  const [tickets, setTickets] = useState([]);
  const [checkedTickets, setCheckedTickets] = useState({});
  const [viewType] = useState("list"); // "list" ou "kanban"
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [, setFilteredTickets] = useState([]);
  // const [selectedProject, setSelectedProject] = useState("Projet 1");
  const [loading, setLoading] = useState(false);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [role, setRole] = useState("Normal User");
  const [openAddProjectModal, setOpenAddProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newPageListCategories, setNewPageListCategories] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [accessibleProjects, setAccessibleProjects] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [filters] = useState({
    type: "",
    priority: "",
    dateAdded: "",
    ticketFilters: "",
  });
  const { projectId: urlProjectId, ticketId: urlTicketId } = useParams();
  const [ticketToEdit, setTicketToEdit] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [projects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(urlProjectId || null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [selectedProjectMembers, setSelectedProjectMembers] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [prioritySort, setPrioritySort] = useState(null);
  const { isPremium } = useIsPremium();
  const [q, setQ] = useState("");
  const FREE_PROJECT_LIMIT = 1;
  const canCreateAnotherProject =
    isPremium || accessibleProjects.length < FREE_PROJECT_LIMIT;

  const navigate = useNavigate();

  useEffect(() => {
    if (urlProjectId && urlProjectId !== selectedProject) {
      setSelectedProject(urlProjectId);
    }
  }, [urlProjectId, selectedProject]);

  const { monthlyCount, monthlyLimit } = useMonthlyTicketUsage(
    selectedProject && selectedProject !== "__all" ? selectedProject : null
  );

  const [errors, setErrors] = useState({
    ticketName: false,
    originalQuestion: false,
    ticketType: false,
    selectedPriority: false,
  });

  const statusTabs = useMemo(
    () => [
      { key: "All", labelKey: "All" },
      { key: "Open", labelKey: "Open" },
      { key: "Working on It", labelKey: "WorkingOnIt" },
      { key: "In Staging", labelKey: "InStaging" },
      { key: "In Test", labelKey: "InTest" },
      { key: "In Review", labelKey: "InReview" },
      { key: "In Production", labelKey: "InProduction" },
      { key: "Closed", labelKey: "Closed" },
    ],
    []
  );

  // état de l'onglet actif (index dans statusTabs)
  const [tabIndex, setTabIndex] = useState(0);

  // calcule le nombre de tickets par statut (et total pour "All")
  const statusCounts = useMemo(() => {
    const counts = { All: tickets.length };
    statusTabs.forEach(({ key }) => {
      if (key !== "All") {
        counts[key] = tickets.filter((t) => t.status === key).length;
      }
    });
    return counts;
  }, [tickets, statusTabs]);

  const displayedTickets = useMemo(() => {
    const { key: activeKey } = statusTabs[tabIndex];

    // 1) Filtre par statut (onglet)
    let list =
      activeKey === "All"
        ? tickets.slice()
        : tickets.filter((t) => t.status === activeKey);

    // 2) Filtre par texte (Premium uniquement)
    if (isPremium && q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter((t) => {
        const name = (t.ticketName || "").toLowerCase();
        const desc = (t.originalQuestion || "").toLowerCase();
        const num = (t.ticketNumber || "").toLowerCase();
        return name.includes(s) || desc.includes(s) || num.includes(s);
      });
    }

    // 3) Tri par priorité si demandé, sinon par date desc
    if (prioritySort) {
      const order = { High: 3, Medium: 2, Low: 1 };
      list.sort((a, b) =>
        prioritySort === "asc"
          ? (order[a.priority] || 0) - (order[b.priority] || 0)
          : (order[b.priority] || 0) - (order[a.priority] || 0)
      );
    } else {
      list.sort(
        (a, b) => (b?.createdAt?.seconds || 0) - (a?.createdAt?.seconds || 0)
      );
    }

    return list;
  }, [tickets, tabIndex, prioritySort, statusTabs, isPremium, q]);

  const handleOpenEditModal = (ticket) => {
    const user = companyUsers.find((u) => u.id === ticket.createdById) || {};
    setTicketToEdit({
      ...ticket,
      creatorName: user.name || user.email || ticket.createdBy,
    });
    // initialise attachments avec les images déjà uploadées
    setAttachments(
      (ticket.images || []).map((url) => ({
        url,
        file: null,
      }))
    );

    navigate(`/app/projects/${selectedProject}/tickets/${ticket.id}`, {
      replace: true,
    });
  };

  const handleCloseEditModal = () => {
    setTicketToEdit(null);
    navigate(`/app/projects/${selectedProject}`);
  };

  const projectOptions = useMemo(
    () => accessibleProjects,
    [accessibleProjects]
  );

  useEffect(() => {
    if (!urlTicketId) return;
    const decodedTicketId = decodeURIComponent(urlTicketId);
    if (tickets.length === 0) return;
    const t = tickets.find((t) => t.id === decodedTicketId);
    if (t) setTicketToEdit(t);
  }, [tickets, urlTicketId]);

  useEffect(() => {
    if (!currentUser) return;

    const profileRef = doc(db, "profilsUsers", currentUser.uid);
    getDoc(profileRef)
      .then((docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setRole(data.role || "Normal User");
          setCurrentUserProfile(data); // ← stocke tout le profil
        } else {
          setRole("Normal User");
        }
      })
      .catch((error) => {
        console.error("Error fetching profile: ", error);
        setRole("Normal User");
      });
  }, [currentUser]);

  useEffect(() => {
    if (!currentUserProfile?.companyId) return;
    const q = query(
      collection(db, "profilsUsers"),
      where("companyId", "==", currentUserProfile.companyId)
    );
    const unsub = onSnapshot(q, (snap) =>
      setCompanyUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => unsub();
  }, [currentUserProfile]);

  useEffect(() => {
    if (!currentUserProfile?.companyId) return;
    const projectsRef = collection(db, "projects");
    const q = query(
      projectsRef,
      where("companyId", "==", currentUserProfile.companyId)
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const projs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllProjects(projs);
    });
    return () => unsub();
  }, [currentUserProfile]);

  useEffect(() => {
    if (!currentUser) return;
    if (role === "Admin") {
      setAccessibleProjects(allProjects);
    } else {
      setAccessibleProjects(
        allProjects.filter(
          (p) =>
            Array.isArray(p.memberIds) && p.memberIds.includes(currentUser.uid)
        )
      );
    }
  }, [allProjects, role, currentUser]);

  useEffect(() => {
    if (!urlProjectId || accessibleProjects.length === 0) return;
    const decoded = decodeURIComponent(urlProjectId);
    // ne setter que si ce projet est bien dans accessibleProjects
    if (accessibleProjects.some((p) => p.id === decoded)) {
      setSelectedProject(decoded);
    }
  }, [urlProjectId, accessibleProjects]);
  useEffect(() => {
    if (currentUser) {
      const profileRef = doc(db, "profilsUsers", currentUser.uid);
      getDoc(profileRef)
        .then((docSnap) => {
          if (docSnap.exists()) {
            setRole(docSnap.data().role);
          } else {
            setRole("Normal User");
          }
        })
        .catch((error) => {
          console.error("Error fetching profile: ", error);
          setRole("Normal User");
        });
    }
  }, [currentUser]);

  function handleProjectChange(id) {
    setSelectedProject(id);
    navigate(`/app/projects/${encodeURIComponent(id)}`, { replace: true });
  }

  const handleCreateProject = async () => {
    if (!isPremium && allProjects.length >= 1) {
      showCustomToast(translate("PremiumNeededForMoreProjects"), "error");
      return;
    }
    if (!newProjectName) return;
    const id = newProjectName.trim();
    const ref = doc(db, "projects", id);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      showCustomToast(translate("ProjectAlreadyExists"), "error");
      return;
    }
    const proj = {
      name: id,
      companyId: currentUserProfile.companyId,
      companyName: currentUserProfile.companyName,
      pageListCategories: newPageListCategories,
      createdAt: new Date(),
      memberIds: [currentUser.uid, ...selectedProjectMembers.map((u) => u.id)],
      memberEmails: [
        currentUser.email,
        ...selectedProjectMembers.map((u) => u.email),
      ],
    };
    await setDoc(ref, proj);
    const counterRef = doc(db, "projects", id, "meta", "counter");
    await setDoc(counterRef, { nextTicketNumber: 0 });
    setOpenAddProjectModal(false);
    setNewProjectName("");
    setNewPageListCategories([]);
    setSelectedProject(id);
    showCustomToast(translate("ProjectCreated"), "success");
  };

  const handleUpdateTicket = async (updatedTicket) => {
    const isCreator = ticketToEdit.createdById === currentUser.uid;
    if (!isCreator && role !== "Admin") {
      showCustomToast(translate("OnlyCreatorCanEdit"), "error");
      return;
    }

    setEditLoading(true);
    try {
      if (Array.isArray(updatedTicket.images)) {
        const userFiles = updatedTicket.images;
        const keptUrls = userFiles.filter((a) => a.url).map((a) => a.url);
        const newUrls = [];
        for (let { file } of userFiles.filter((a) => a.file)) {
          const path = `tickets/${ticketToEdit.id}/${file.name}`;
          const snap = await uploadBytes(storageRef(storage, path), file);
          newUrls.push(await getDownloadURL(snap.ref));
        }

        const imagesField = [...keptUrls, ...newUrls];
        const ticketDocRef = doc(
          db,
          "projects",
          selectedProject,
          "tickets",
          ticketToEdit.id
        );

        const oldUrls = ticketToEdit.images || [];
        const added = imagesField.filter((u) => !oldUrls.includes(u));
        if (added.length > 0) {
          await addDoc(
            collection(
              db,
              "projects",
              selectedProject,
              "tickets",
              ticketToEdit.id,
              "activity"
            ),
            {
              action: `Pièces jointes ajoutées (${added.length})`,
              performedBy: currentUser.displayName,
              timestamp: new Date(),
            }
          );
        }

        if (role === "Admin" && updatedTicket.status !== ticketToEdit.status) {
          await updateDoc(ticketDocRef, {
            status: updatedTicket.status,
            images: imagesField,
          });
          await addDoc(
            collection(
              db,
              "projects",
              selectedProject,
              "tickets",
              ticketToEdit.id,
              "activity"
            ),
            {
              action: `Statut → ${ticketToEdit.status} → ${updatedTicket.status}`,
              performedBy: currentUser.displayName,
              timestamp: new Date(),
            }
          );
          showCustomToast(translate("TicketUpdatedSuccessfully"), "success");
        } else {
          await updateDoc(ticketDocRef, { images: imagesField });
          showCustomToast(translate("AttachmentsSaved"), "success");
        }

        handleCloseEditModal();
        setEditLoading(false);
        return;
      }
      // ────────────────────────────────────────────────────

      const oldUrls = ticketToEdit.images || [];
      const keptUrls = attachments.filter((a) => a.url).map((a) => a.url);
      for (let url of oldUrls.filter((u) => !keptUrls.includes(u))) {
        await deleteObject(storageRef(storage, url)).catch(() => {});
      }

      const newUrls = [];
      for (let { file } of attachments.filter((a) => a.file)) {
        const path = `tickets/${ticketToEdit.id}/${file.name}`;
        const snap = await uploadBytes(storageRef(storage, path), file);
        newUrls.push(await getDownloadURL(snap.ref));
      }

      const imagesField = [...keptUrls, ...newUrls];
      const ticketDocRef = doc(
        db,
        "projects",
        selectedProject,
        "tickets",
        ticketToEdit.id
      );

      if (role === "Admin") {
        await updateDoc(ticketDocRef, {
          status: updatedTicket.status,
          images: imagesField,
        });
        await addDoc(
          collection(
            db,
            "projects",
            selectedProject,
            "tickets",
            ticketToEdit.id,
            "activity"
          ),
          {
            action: `Statut → ${ticketToEdit.status} → ${updatedTicket.status}`,
            performedBy: currentUser.displayName,
            timestamp: new Date(),
          }
        );
        showCustomToast(translate("TicketUpdatedSuccessfully"), "success");
        handleCloseEditModal();
        setEditLoading(false);
        return;
      }

      const changes = [];
      if (
        updatedTicket.status &&
        updatedTicket.status !== ticketToEdit.status
      ) {
        changes.push(
          `Statut → ${ticketToEdit.status} → ${updatedTicket.status}`
        );
      }
      if (
        updatedTicket.selectedPriority &&
        updatedTicket.selectedPriority !== ticketToEdit.priority
      ) {
        changes.push(
          `Priorité → ${ticketToEdit.priority} → ${updatedTicket.selectedPriority}`
        );
      }
      if (
        updatedTicket.originalQuestion &&
        updatedTicket.originalQuestion !== ticketToEdit.originalQuestion
      ) {
        changes.push(`Description modifiée`);
      }
      if (
        updatedTicket.ticketType &&
        updatedTicket.ticketType !== ticketToEdit.ticketType
      ) {
        changes.push(
          `Type → ${ticketToEdit.ticketType} → ${updatedTicket.ticketType}`
        );
      }
      if (
        updatedTicket.pageList &&
        updatedTicket.pageList !== ticketToEdit.pageList
      ) {
        changes.push(
          `Élément concerné → ${ticketToEdit.pageList} → ${updatedTicket.pageList}`
        );
      }

      const updateData = {
        ticketName: updatedTicket.ticketName,
        ticketType: updatedTicket.ticketType,
        originalQuestion: updatedTicket.originalQuestion,
        pageList: updatedTicket.pageList,
        priority: updatedTicket.selectedPriority,
        ...(updatedTicket.status && { status: updatedTicket.status }),
      };

      await updateDoc(ticketDocRef, {
        ...updateData,
        images: imagesField,
      });

      for (let change of changes) {
        const formatted = change.replace(" → ", " : ").replace(" → ", " → ");
        await addDoc(
          collection(
            db,
            "projects",
            selectedProject,
            "tickets",
            ticketToEdit.id,
            "activity"
          ),
          {
            action: formatted,
            performedBy: currentUser.displayName,
            timestamp: new Date(),
          }
        );
      }

      showCustomToast(translate("TicketUpdatedSuccessfully"), "success");
      handleCloseEditModal();
    } catch (error) {
      showCustomToast(translate("FailedToUpdateTicket"), "error");
      console.error("Error updating document:", error);
    } finally {
      setEditLoading(false);
    }
  };

  const handleOpenDeleteModal = (ticketId) => {
    setTicketToDelete(ticketId);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setTicketToDelete(null);
  };

  useEffect(() => {
    if (currentUser) {
      setClientIdState(currentUser.uid);
    }
  }, [currentUser]);

  const handleDragEnd = async (result) => {
    console.log("🛞 dragEnd:", result);
    console.log("🏷️ known droppables:", [
      "Open",
      "Working on It",
      "In Staging",
      "In Test",
      "In Production",
      "Closed",
    ]);
    console.log(
      "📋 tickets statuses:",
      tickets.map((t) => `${t.id}:${t.status}`)
    );
    const { source, destination, draggableId } = result;
    if (!destination || destination.droppableId === source.droppableId) return;

    const newStatus = destination.droppableId;
    setTickets((prev) =>
      prev.map((t) =>
        t.id === draggableId
          ? {
              ...t,
              status: newStatus,
            }
          : t
      )
    );

    const docRef = doc(db, "projects", selectedProject, "tickets", draggableId);

    updateDoc(docRef, { status: newStatus }).catch(console.error);
  };

  useEffect(() => {
    if (!currentUser || !currentUserProfile || !selectedProject) {
      setTickets([]);
      setTicketsLoading(false);
      return;
    }

    setTicketsLoading(true);

    let q;
    if (role === "Admin" && selectedProject === "__all") {
      q = query(
        collectionGroup(db, "tickets"),
        where("companyId", "==", currentUserProfile.companyId)
      );
    } else {
      const ticketsRef = collection(db, "projects", selectedProject, "tickets");
      q = query(ticketsRef);
    }

    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))

        .sort((a, b) => b.createdAt.seconds - a.createdAt.seconds);
      setTickets(docs);
      setTicketsLoading(false);
    });

    // 3️⃣ nettoyage
    return () => unsub();
  }, [
    currentUser,
    currentUserProfile,
    role,
    selectedProject, // dès que ce change, on refait la même logique
  ]);

  useEffect(() => {
    const applyFilters = () => {
      let filtered = tickets;

      if (filters.type) {
        filtered = filtered.filter(
          (ticket) => ticket.ticketType === filters.type
        );
      }

      if (filters.priority) {
        filtered = filtered.filter(
          (ticket) => ticket.priority === filters.priority
        );
      }

      if (filters.ticketFilters) {
        filtered = filtered.filter(
          (ticket) => ticket.status === filters.ticketFilters
        );
      }

      if (filters.dateAdded) {
        const today = new Date();
        filtered = filtered.filter((ticket) => {
          const ticketDate = new Date(ticket.createdAt.seconds * 1000);
          if (filters.dateAdded === "Today") {
            return ticketDate.toDateString() === today.toDateString();
          } else if (filters.dateAdded === "Last Week") {
            const oneWeekAgo = new Date(today.setDate(today.getDate() - 7));
            return ticketDate >= oneWeekAgo;
          } else if (filters.dateAdded === "Last Month") {
            const oneMonthAgo = new Date(today.setMonth(today.getMonth() - 1));
            return ticketDate >= oneMonthAgo;
          }
          return true;
        });
      }

      filtered.sort((a, b) => b.createdAt.seconds - a.createdAt.seconds);
      setFilteredTickets(filtered);
    };

    applyFilters();
  }, [tickets, filters]);

  const resetForm = () => {
    setTicketName("");
    setTicketType("");
    setOriginalQuestion("");
    setPageList("");
    setScreenshots(null);
    setSelectedPriority("Low");
  };

  const handleOpenAddTicketModal = () => {
    setSelectedCard(null);
    resetForm();
    setOpenAddClientModal(true);
  };

  const handleCloseAddTicketModal = () => {
    if (!selectedCard) {
      resetForm();
    }
    setOpenAddClientModal(false);
  };

  const handleSubmit = async () => {
    if (role !== "Normal User" && role !== "Admin") {
      showCustomToast(translate("MustBeActiveUser"), "error");
      return;
    }
    // === VALIDATION ===
    const missing = [];
    if (!ticketName.trim()) missing.push(translate("TicketNameError"));
    if (!originalQuestion.trim())
      missing.push(translate("WriteDescriptionError"));
    if (!ticketType) missing.push(translate("TicketTypeError"));
    if (!selectedPriority) missing.push(translate("PriorityError"));
    if (missing.length > 0) {
      showCustomToast(
        `${translate("MissingFields")}: ${missing.join(", ")}`,
        "error"
      );
      setErrors({
        ticketName: !ticketName.trim(),
        originalQuestion: !originalQuestion.trim(),
        ticketType: !ticketType,
        selectedPriority: !selectedPriority,
      });
      return;
    }

    setLoading(true);
    try {
      if (!selectedProject) {
        showCustomToast(translate("SelectProject"), "error");
        setLoading(false);
        return;
      }

      if (!isPremium && monthlyCount >= monthlyLimit) {
        showCustomToast(translate("MonthlyTicketCapReached"), "error");
        setLoading(false);
        return;
      }

      const counterRef = doc(db, "meta", "ticketCounter");
      let generatedNumber;

      // 2. Transaction atomique sur le compteur global
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(counterRef);
        // Si absent, on démarre à 0, sinon on incrémente
        const next = (snap.exists() ? snap.data().nextTicketNumber : 0) + 1;
        tx.set(counterRef, { nextTicketNumber: next }, { merge: true });

        // 3. Format : préfixe projet + 6 chiffres
        const prefix = selectedProject.toUpperCase().slice(0, 4);
        generatedNumber = `${prefix}-${String(next).padStart(6, "0")}`;
      });

      const newTicket = {
        ticketName,
        ticketType,
        originalQuestion,
        pageList,
        priority: selectedPriority,
        createdBy: currentUser?.displayName || "Anonymous",
        createdById: currentUser.uid,
        createdAt: new Date(),
        status: "Open",
        ticketNumber: generatedNumber,
        companyId: currentUserProfile.companyId,
        companyName: currentUserProfile.companyName,
        projectId: selectedProject,
      };

      // const ticketRef = await addDoc(collection(db, "tickets"), newTicket);
      const ticketRef = await addDoc(
        collection(db, "projects", selectedProject, "tickets"),
        newTicket
      );

      // upload des attachments
      const imageUrls = [];
      for (let { file } of attachments) {
        const path = `tickets/${ticketRef.id}/${file.name}`;
        const snap = await uploadBytes(storageRef(storage, path), file);
        imageUrls.push(await getDownloadURL(snap.ref));
      }
      if (imageUrls.length) {
        await updateDoc(ticketRef, { images: imageUrls });
      }

      await addDoc(
        collection(
          db,
          "projects",
          selectedProject,
          "tickets",
          ticketRef.id,
          "activity"
        ),
        {
          action: "Ticket créé",
          performedBy: currentUser.displayName,
          timestamp: new Date(),
        }
      );
      setAttachments([]);
      showCustomToast(translate("TicketAddedSuccessfully"), "success");
      handleCloseAddTicketModal();
    } catch (error) {
      showCustomToast(translate("FailedToAddTicket"), "error");
      console.error("Error adding ticket: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTicket = (ticketId) => {
    handleOpenDeleteModal(ticketId); // Ouvre le modal et stocke l'ID du ticket à supprimer
  };

  const handleCheckboxChange = (ticketId) => {
    setCheckedTickets((prevState) => ({
      ...prevState,
      [ticketId]: !prevState[ticketId], // Inverser l'état de la case cochée
    }));
  };

  const headerStyle = {
    display: "flex",
    justifyContent: "flex-start", // plus d'espace entre
    boxSizing: "border-box",
    paddingTop: "15px",
    paddingBottom: "5px",
    borderBottom: "2px solid lightgrey",
    fontWeight: "bold",
    fontSize: "12px",
    width: "100%",
  };

  const commonCellStyle = {
    textAlign: "left",
    paddingLeft: "5px",
  };

  const headerCells = [
    { label: translate("Creator"), width: "17%" },
    { label: translate("TicketNumber"), width: "7%" },
    { label: translate("TicketName"), width: "26%" },
    { label: translate("Type"), width: "14%" },
    { label: translate("Priority"), width: "14%" },
    // { label: translate("PageConcerned"), width: "14%" },
    { label: translate("Status"), width: "10%" },
    { label: translate("CreatedAt"), width: "12%" },
  ];

  const adminHeaderCells = [
    { label: translate("Project"), width: "13%" },
    { label: translate("Creator"), width: "13%" },
    { label: translate("TicketNumber"), width: "8%" },
    { label: translate("TicketName"), width: "26%" },
    { label: translate("Type"), width: "10%" },
    { label: translate("Priority"), width: "10%" },
    // { label: translate("PageConcerned"), width: "10%" },
    { label: translate("Status"), width: "8%" },
    { label: translate("CreatedAt"), width: "12%" },
  ];

  // === constantes partagées pour dropdowns ===
  const TICKET_TYPES = [
    "Incident",
    "Problem",
    "Question",
    "Suggestion",
    "Evolution",
    "Bug",
    "Feature",
    "Improvement",
    "ChangeRequest",
    "Task",
  ];

  const PRIORITY_COLORS = {
    Low: "green",
    Medium: "orange",
    High: "red",
  };

  const PRIORITY_LEVELS = ["Low", "Medium", "High"];

  useEffect(() => {
    // Filtre les tickets selon les filtres sélectionnés
    const applyFilters = () => {
      let filtered = tickets;

      if (filters.type) {
        filtered = filtered.filter(
          (ticket) => ticket.ticketType === filters.type
        );
      }

      if (filters.priority) {
        filtered = filtered.filter(
          (ticket) => ticket.priority === filters.priority
        );
      }

      if (filters.ticketFilters) {
        filtered = filtered.filter(
          (ticket) => ticket.status === filters.ticketFilters
        );
      }

      if (filters.dateAdded) {
        const today = new Date();
        filtered = filtered.filter((ticket) => {
          const ticketDate = new Date(ticket.createdAt.seconds * 1000);
          if (filters.dateAdded === "Today") {
            return ticketDate.toDateString() === today.toDateString();
          } else if (filters.dateAdded === "Last Week") {
            const oneWeekAgo = new Date(today.setDate(today.getDate() - 7));
            return ticketDate >= oneWeekAgo;
          } else if (filters.dateAdded === "Last Month") {
            const oneMonthAgo = new Date(today.setMonth(today.getMonth() - 1));
            return ticketDate >= oneMonthAgo;
          }
          return true;
        });
      }

      filtered.sort((a, b) => b.createdAt.seconds - a.createdAt.seconds);
      setFilteredTickets(filtered);
    };

    applyFilters();
  }, [tickets, filters]);

  return (
    <Box sx={{ display: "flex", width: "100%", bgcolor: "background.default" }}>
      <LeftMenu />
      <Box sx={{ width: "90%", boxSizing: "border-box", ml: "5px" }}>
        {projects.length > 0 && !selectedProject && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "60vh",
              px: 2,
            }}
          >
            {/* Flèche à gauche */}
            <Box
              component="img"
              src={ArrowHand}
              alt="arrow"
              sx={{ width: 48, height: 48, mr: 2 }}
            />

            {/* Carte de démarrage */}
            <Card
              sx={{
                maxWidth: 400,
                textAlign: "center",
                p: 2,
                bgcolor: "background.paper",
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {role === "Admin"
                    ? translate("AdminNoProjectsYet")
                    : translate("NoProjectSelected")}
                </Typography>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
          {translate("GetStartedByCreatingProject")}
        </Typography>
        {role === "Admin" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Tooltip
              title={
                !canCreateAnotherProject
                  ? translate("ProjectLimitReached")
                  : ""
              }
              placement="top"
            >
              <span>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => {
                    if (!canCreateAnotherProject) {
                      showCustomToast(
                        translate("PremiumNeededForMoreProjects"),
                        "error"
                      );
                      return;
                    }
                    setOpenAddProjectModal(true);
                  }}
                  disabled={!canCreateAnotherProject}
                >
                  {translate("AddAProject")}
                </Button>
              </span>
            </Tooltip>
            {!canCreateAnotherProject && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="error">
                  {translate("ProjectLimitReached") ||
                    "Limite atteinte sur le plan Gratuit (1 projet)"}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => navigate("/app/gotopremium")}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  {translate("GoPremium") || "Passer Premium"}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  </Box>
)}

        <HeaderBar
          title={translate("SupportTickets")}
          subtitle={translate("PleaseSendTicket")}
          projects={projectOptions}
          selectedProject={selectedProject}
          onProjectChange={handleProjectChange}
          onAddProjectClick={() => {
            if (!canCreateAnotherProject) {
              showCustomToast(
                translate("PremiumNeededForMoreProjects"),
                "error"
              );
              return;
            }
            setOpenAddProjectModal(true);
          }}
          showProjectSelect={true}
          showAddProjectButton={role === "Admin"}
          addProjectDisabled={!canCreateAnotherProject}
          addProjectTooltip={
            translate("PremiumNeededForMoreProjects") ||
            "Limite atteinte sur le plan Gratuit (1 projet)"
          }
          addProjectCtaLabel={translate("GoPremium") || "Passer Premium"}
          onAddProjectCta={() => navigate("/app/gotopremium")}
          userName={currentUser.displayName}
          userRole={role}
          showSearch
          searchValue={q}
          onSearchChange={setQ}
        >
          {(role === "Normal User" || role === "Admin") && selectedProject && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAddTicketModal}
              sx={{ ml: 1, height: 36 }}
            >
              {translate("AddNewTicket")}
            </Button>
          )}
        </HeaderBar>

        {/* === Nos nouveaux onglets de statut === */}
        <Box sx={{ pl: "20px", display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              px: 3,
              mt: 2,
              bgcolor: "background.paper",
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
              width: "89%",
            }}
          >
            <Tabs
              value={tabIndex}
              onChange={(_, idx) => setTabIndex(idx)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {statusTabs.map(({ key, labelKey }) => (
                <Tab
                  key={key}
                  label={`${translate(labelKey)} (${statusCounts[key] || 0})`}
                />
              ))}
            </Tabs>
          </Box>

          <div
            style={{
              width: "90%",
              paddingLeft: "0px",
              paddingRight: "30px",
              display: "flex",
              flexDirection: "row",
              bgcolor: "background.paper",
              borderBottomLeftRadius: "20px",
              borderBottomRightRadius: "20px",
              paddingBottom: "10px",
            }}
          >
            {tickets.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "60vh",
                  px: 2,
                  width: "100%",
                }}
              >
                <Card
                  sx={{
                    maxWidth: 400,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 2,
                  }}
                >
                  {/* Flèche manuscrite bleue à gauche */}
                  <Box
                    component="img"
                    src={ArrowHand}
                    alt=""
                    sx={{ width: 72, height: 72 }}
                  />

                  {/* Texte centré */}
                  <CardContent sx={{ textAlign: "center" }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {translate("NoTicketsToShow")}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {translate("TryAnotherProjectOrTab")}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <div
                style={{
                  paddingRight: "20px",
                  width: "100%",
                  paddingLeft: "30px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    height: "70vh",
                    // border: "1px solid lightgrey",
                    borderRadius: "10px",
                    paddingTop: "20px",
                    paddingLeft: "0px",
                    // overflowY: "auto",
                  }}
                >
                  {viewType === "list" && (
                    <div style={{ ...headerStyle, width: "100%" }}>
                      {(role === "Admin" ? adminHeaderCells : headerCells).map(
                        (cell, idx) => {
                          const isPriority =
                            cell.label === translate("Priority");
                          return (
                            <div
                              key={idx}
                              style={{
                                width: cell.width,
                                ...commonCellStyle,
                                textAlign: cell.textAlign || "center",
                                paddingLeft: cell.paddingLeft || "0px",
                                bgcolor: "action.hover",
                              }}
                            >
                              {isPriority ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                    gap: 0.5,
                                    alignContent: "start",
                                    justifyContent: "center",
                                  }}
                                >
                                  <div variant="body2">{cell.label}</div>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "row",
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setPrioritySort((prev) =>
                                          prev === "asc" ? null : "asc"
                                        )
                                      }
                                      sx={{ padding: "2px" }}
                                    >
                                      <ArrowUpwardIcon fontSize="inherit" />
                                    </IconButton>

                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setPrioritySort((prev) =>
                                          prev === "desc" ? null : "desc"
                                        )
                                      }
                                      sx={{ padding: "2px" }}
                                    >
                                      <ArrowDownwardIcon fontSize="inherit" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              ) : (
                                cell.label
                              )}
                            </div>
                          );
                        }
                      )}
                    </div>
                  )}
                  {/* <div style={{ marginTop: "10px" }}> */}
                  <div
                    style={{
                      marginTop: "10px",
                      height: "90vh",
                      overflowY: "auto",
                    }}
                  >
                    <div
                      style={{
                        marginTop: "0px",
                        marginLeft: "0px",
                        marginRight: "0px",
                      }}
                    >
                      {/* {ticketsLoading ? ( */}

                      {viewType === "list" ? (
                        ticketsLoading ? (
                          <>
                            <Skeleton
                              variant="rectangular"
                              width="100%"
                              height={40}
                              style={{ marginBottom: 8 }}
                            />
                            <Skeleton
                              variant="rectangular"
                              width="100%"
                              height={40}
                              style={{ marginBottom: 8 }}
                            />
                            <Skeleton
                              variant="rectangular"
                              width="100%"
                              height={40}
                              style={{ marginBottom: 8 }}
                            />
                          </>
                        ) : displayedTickets.length > 0 ? (
                          displayedTickets.map((ticket) => {
                            const checked = !!checkedTickets[ticket.id];
                            // const canEdit =
                            //   ticket.createdById === currentUser.uid;

                            const proj =
                              allProjects.find(
                                (p) => p.id === ticket.projectId
                              ) || {};
                            const creator = companyUsers.find(
                              (u) => u.id === ticket.createdById
                            ) || {
                              email: ticket.createdBy, // fallback
                              photoUrl: null,
                            };
                            const isCreator =
                              ticket.createdById === currentUser.uid;

                            const deleteHandler = isCreator
                              ? handleDeleteTicket
                              : undefined;

                            if (role === "Admin") {
                              return (
                                <TicketLineAdmin
                                  key={ticket.id}
                                  ticket={ticket}
                                  projectName={proj.name}
                                  checked={checked}
                                  onCheckboxChange={handleCheckboxChange}
                                  onEdit={() => handleOpenEditModal(ticket)}
                                  onDelete={deleteHandler}
                                  creator={creator}
                                />
                              );
                            }
                            return (
                              <TicketLine
                                key={ticket.id}
                                ticket={ticket}
                                checked={checked}
                                creator={creator}
                                canEdit={true}
                                onCheckboxChange={handleCheckboxChange}
                                onEdit={() => handleOpenEditModal(ticket)}
                                onDelete={deleteHandler}
                              />
                            );
                          })
                        ) : (
                          <Box sx={{ textAlign: "center", py: 10 }}>
                            <Card
                              sx={{
                                maxWidth: 450,
                                mx: "auto",
                                p: 2,
                                bgcolor: "background.paper",
                              }}
                            >
                              <CardContent>
                                <Typography
                                  variant="h6"
                                  fontWeight="bold"
                                  gutterBottom
                                >
                                  {translate("NoTicketsInTab", {
                                    tab: translate(statusTabs[tabIndex]),
                                  })}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  {translate("NoTicketsInTabMessage")}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Box>
                        )
                      ) : (
                        <DragDropContext
                          key={viewType} // ← ici on force un démontage/remontage à chaque switch
                          onDragEnd={handleDragEnd}
                        >
                          <Box display="flex" px={3}>
                            {[
                              { key: "Open", color: "#28a745" },
                              { key: "Working on It", color: "#17a2b8" },
                              { key: "In Staging", color: "#6610f2" },
                              { key: "In Test", color: "#fd7e14" },
                              { key: "In Production", color: "#20c997" },
                              { key: "Closed", color: "#dc3545" },
                            ].map(({ key, color }) => {
                              // 🚩 Ici on est dans un bloc JS, on peut logger puis retourner
                              console.log(
                                `[Tickets] about to render Column for status="${key}", count=`,
                                displayedTickets.filter((t) => t.status === key)
                                  .length
                              );
                              return (
                                <Column
                                  key={key}
                                  id={key}
                                  title={translate(key)}
                                  borderColor={color}
                                  tasks={displayedTickets.filter(
                                    (t) => t.status === key
                                  )}
                                  renderSkeleton={ticketsLoading}
                                  onSelectCard={(ticket) => {
                                    setTicketToEdit(ticket);
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </DragDropContext>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Box>

        <Modal
          open={openAddProjectModal}
          onClose={() => setOpenAddProjectModal(false)}
        >
          <Slide direction="left" in={openAddProjectModal}>
            <Box
              sx={{
                position: "fixed",
                top: "5%",
                right: 0,
                width: "40%",
                height: "90%",
                bgcolor: "background.paper",
                boxShadow: 24,
                borderRadius: "15px",
                pl: 2,
                pr: 2,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                paddingBottom: "10px",
              }}
            >
              <div style={{ width: "100%" }}>
                <h2>{translate("AddProject")}</h2>

                <h4>{translate("ExplainationAddProject")} </h4>
                <TextField
                  label={translate("ProjectName")}
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  sx={{ width: "50%" }}
                />

                <div style={{ marginTop: "20px" }}>
                  <Autocomplete
                    multiple
                    // options={companyUsers}
                    options={companyUsers.filter(
                      (u) => u.id !== currentUser.uid
                    )}
                    // getOptionLabel={(u) => u.name || u.email}
                    getOptionLabel={(u) =>
                      u.name ? `${u.name} (${u.email})` : u.email
                    }
                    value={selectedProjectMembers}
                    onChange={(e, newValue) =>
                      setSelectedProjectMembers(newValue)
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Avatar
                          src={option.photoUrl}
                          sx={{ width: 24, height: 24, mr: 1 }}
                        />
                        {/* {option.name || option.email} */}
                        {option.name
                          ? `${option.name} (${option.email})`
                          : option.email}
                      </li>
                    )}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, idx) => (
                        <Chip
                          {...getTagProps({ index: idx })}
                          key={option.id}
                          avatar={
                            <Avatar
                              src={option.photoUrl}
                              sx={{ width: 20, height: 20 }}
                            />
                          }
                          // affiche Nom (email)
                          label={
                            option.name
                              ? `${option.name} (${option.email})`
                              : option.email
                          }
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={translate("ProjectMembers")}
                        placeholder={translate("SelectMembers")}
                      />
                    )}
                  />
                </div>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {translate("OptionalMemberInfo")}
                </Typography>
              </div>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button onClick={() => setOpenAddProjectModal(false)}>
                  {translate("Cancel")}
                </Button>
                <Button onClick={handleCreateProject} sx={{ ml: 1 }}>
                  {translate("Create")}
                </Button>
              </Box>
            </Box>
          </Slide>
        </Modal>

        <EditTicketModal
          // open={openEditModal}
          open={Boolean(urlProjectId && urlTicketId)}
          onClose={() => {
            navigate(`/app/projects/${encodeURIComponent(selectedProject)}`, {
              replace: true,
            });
            setTicketToEdit(null);
          }}
          ticket={ticketToEdit}
          handleUpdate={handleUpdateTicket}
          loading={editLoading}
          role={role}
          selectedProject={selectedProject}
          attachments={attachments}
          setAttachments={setAttachments}
        />

        <DeleteTicketModal
          open={deleteModalOpen}
          onClose={handleCloseDeleteModal}
          ticketId={ticketToDelete}
          selectedProject={selectedProject}
        />

        <Modal
          // open={openAddClientModal && role === "Normal User"}
          open={openAddClientModal /* on ne filtre plus sur le rôle */}
          onClose={handleCloseAddTicketModal}
          aria-labelledby="add-client-modal-title"
          aria-describedby="add-client-modal-description"
          closeAfterTransition
        >
          <Slide direction="left" in={openAddClientModal}>
            <Box
              sx={{
                position: "fixed",
                top: "5%",
                right: 0,
                width: "40%",
                height: "90%",
                bgcolor: "background.paper",
                boxShadow: 24,
                marginRight: "15px",
                pl: "2",
                pr: "2",
                borderRadius: "15px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0,
                  borderBottom: "1px solid lightgray",
                }}
              >
                <div
                  id="add-client-modal-title"
                  style={{
                    paddingLeft: "30px",
                    paddingTop: "10px",
                    paddingBottom: "10px",
                    fontSize: "22px",
                    fontWeight: "700",
                  }}
                >
                  {selectedCard
                    ? translate("UpdateTicket")
                    : translate("AddNewTicket")}
                </div>
                <div style={{ paddingRight: "15px" }}>
                  <IconButton onClick={handleCloseAddTicketModal}>
                    <CloseIcon />
                  </IconButton>
                </div>
              </Box>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    width: "50%",
                    px: 3,
                    pt: 3,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {/* Projet */}
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel id="ticket-project-label">
                      {translate("Project")}
                    </InputLabel>
                    <Select
                      labelId="ticket-project-label"
                      value={selectedProject}
                      onChange={(e) => setSelectedProject(e.target.value)}
                      label={translate("Project")}
                    >
                      {accessibleProjects.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Nom du ticket */}
                  <Typography variant="subtitle2" fontWeight={600}>
                    {translate("TicketNameForm")}
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    value={ticketName}
                    onChange={(e) => setTicketName(e.target.value)}
                    error={errors.ticketName}
                    helperText={errors.ticketName && translate("RequiredField")}
                    size="small"
                  />

                  {/* Description */}
                  <Typography variant="subtitle2" fontWeight={600}>
                    {translate("WriteDescription")}
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    value={originalQuestion}
                    onChange={(e) => setOriginalQuestion(e.target.value)}
                    error={errors.originalQuestion}
                    helperText={
                      errors.originalQuestion && translate("RequiredField")
                    }
                    size="small"
                  />

                  {/* Priorités cliquables comme dans EditTicketModal */}
                  <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                    {PRIORITY_LEVELS.map((level) => (
                      <Box
                        key={level}
                        onClick={() => {
                          setSelectedPriority(level);
                          if (errors.selectedPriority)
                            setErrors((f) => ({
                              ...f,
                              selectedPriority: false,
                            }));
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 2,
                          py: 1,
                          borderRadius: "20px",
                          border:
                            selectedPriority === level
                              ? "2px solid"
                              : "1px solid lightgrey",
                          borderColor:
                            selectedPriority === level
                              ? PRIORITY_COLORS[level]
                              : "lightgrey",
                          cursor: "pointer",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            backgroundColor: PRIORITY_COLORS[level],
                            width: 15,
                            height: 15,
                            borderRadius: "50%",
                          }}
                        />
                        <Typography variant="body2">
                          {translate(level)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  {errors.selectedPriority && (
                    <FormHelperText error>
                      {translate("RequiredField")}
                    </FormHelperText>
                  )}

                  {/* Gestion des fichiers */}
                  <Button
                    variant="outlined"
                    onClick={() => setUploadModalOpen(true)}
                  >
                    {translate("ManageFile")}
                  </Button>
                  <UploadFilesModal
                    open={uploadModalOpen}
                    onClose={() => setUploadModalOpen(false)}
                    files={attachments}
                    setFiles={setAttachments}
                  />

                  {/* après <UploadFilesModal … /> */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      mt: 1,
                      borderRadius: "5px",
                      border: "1px dashed lightgrey",
                      p: 1,
                      overflowX: "auto",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {attachments.map((a, i) => (
                      <Box
                        key={i}
                        component="a"
                        href={a.file ? a.preview : a.url}
                        target="_blank"
                        sx={{ p: 0, display: "inline-block" }}
                      >
                        <Avatar
                          variant="rounded"
                          src={a.preview || a.url}
                          sx={{ width: 56, height: 56 }}
                        />
                      </Box>
                    ))}
                  </Box>

                  {/* Type de ticket */}
                  <FormControl
                    variant="outlined"
                    fullWidth
                    error={errors.ticketType}
                    size="small"
                  >
                    <InputLabel>{translate("TicketType")}</InputLabel>
                    <Select
                      value={ticketType}
                      onChange={(e) => {
                        setTicketType(e.target.value);
                        if (errors.ticketType)
                          setErrors((f) => ({ ...f, ticketType: false }));
                      }}
                      label={translate("TicketType")}
                    >
                      {TICKET_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {translate(type)}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.ticketType && (
                      <FormHelperText>
                        {translate("RequiredField")}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Box>
                {/* gauche vide (placeholder ou preview) */}
                <Box sx={{ width: "50%" }} />
              </div>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: 0,
                  paddingLeft: "30px",
                  paddingRight: "30px",
                  paddingBottom: "20px",
                  paddingTop: "20px",
                  // borderTop: "1px solid lightgray",
                  borderTop: 1,
                  borderColor: "divider",
                  alignContent: "center",
                  alignItems: "center",
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  style={{ marginRight: "10px", height: "30px" }}
                  onClick={handleCloseAddTicketModal}
                >
                  {translate("Cancel")}
                </Button>

                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : selectedCard ? (
                    translate("UpdateTicket")
                  ) : (
                    translate("AddNewTicket")
                  )}
                </Button>
              </Box>
            </Box>
          </Slide>
        </Modal>
      </Box>
    </Box>
  );
}
